/**
 * Visit Notes Tab Component for Client Detail Page
 * Displays caregiver visit notes with vital signs and services
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Plus, ClipboardList, Clock, FileText, Download, PenTool, CheckCircle2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VISIT_TYPES = [
  { value: "routine", label: "Routine Visit" },
  { value: "assessment", label: "Assessment" },
  { value: "admission", label: "Admission" },
  { value: "discharge", label: "Discharge" },
  { value: "follow_up", label: "Follow Up" },
];

export function VisitNotesTab({
  visitNotes,
  onAddNote,
  clientId,
  currentUser
}) {
  const [weeklySheetDialogOpen, setWeeklySheetDialogOpen] = useState(false);
  const [weekStartDate, setWeekStartDate] = useState(() => {
    // Default to last Sunday
    const today = new Date();
    const day = today.getDay();
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - day);
    return lastSunday.toISOString().split('T')[0];
  });
  const [generatingSheet, setGeneratingSheet] = useState(false);
  
  // RN Signature states
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [rnSignature, setRnSignature] = useState(null);
  const [existingSignature, setExistingSignature] = useState(null);
  const [signingSheet, setSigningSheet] = useState(false);
  const [loadingSignature, setLoadingSignature] = useState(false);

  // Check if user can sign (RN or Admin only)
  const canSign = currentUser?.role === 'rn' || currentUser?.role === 'admin';

  // Fetch existing signature when week changes
  const fetchExistingSignature = useCallback(async () => {
    if (!clientId || !weekStartDate) return;
    
    setLoadingSignature(true);
    try {
      const response = await axios.get(
        `${API}/master-pcw-sheet/signature/${clientId}?week_start=${weekStartDate}`
      );
      if (response.data && response.data.rn_signature) {
        setExistingSignature(response.data);
      } else {
        setExistingSignature(null);
      }
    } catch (error) {
      console.error("Error fetching signature:", error);
      setExistingSignature(null);
    } finally {
      setLoadingSignature(false);
    }
  }, [clientId, weekStartDate]);

  useEffect(() => {
    if (weeklySheetDialogOpen) {
      fetchExistingSignature();
    }
  }, [weeklySheetDialogOpen, weekStartDate, fetchExistingSignature]);

  const getVisitTypeLabel = (type) => {
    return VISIT_TYPES.find(t => t.value === type)?.label || type;
  };

  const handleGenerateWeeklySheet = async () => {
    setGeneratingSheet(true);
    try {
      const response = await axios.get(
        `${API}/master-pcw-sheet/generate/${clientId}?week_start=${weekStartDate}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Master_PCW_Sheet_${weekStartDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Weekly Master PCW Sheet generated!");
    } catch (error) {
      console.error("Failed to generate weekly sheet:", error);
      toast.error("Failed to generate weekly sheet");
    } finally {
      setGeneratingSheet(false);
    }
  };

  const handleRnSign = async () => {
    if (!rnSignature) {
      toast.error("Please provide your signature first");
      return;
    }

    setSigningSheet(true);
    try {
      await axios.post(`${API}/master-pcw-sheet/sign/${clientId}`, {
        week_start: weekStartDate,
        rn_signature: rnSignature,
        rn_name: currentUser?.name || "RN"
      });
      
      toast.success("Master PCW Sheet signed successfully!");
      setShowSignaturePad(false);
      setRnSignature(null);
      // Refresh to show the signature status
      fetchExistingSignature();
    } catch (error) {
      console.error("Failed to sign sheet:", error);
      toast.error(error.response?.data?.detail || "Failed to sign sheet");
    } finally {
      setSigningSheet(false);
    }
  };

  const handleSignatureCapture = (signatureData) => {
    setRnSignature(signatureData);
  };

  const handleClearSignature = () => {
    setRnSignature(null);
  };

  const closeDialog = () => {
    setWeeklySheetDialogOpen(false);
    setShowSignaturePad(false);
    setRnSignature(null);
  };

  return (
    <div data-testid="client-visit-notes-tab">
      <div className="form-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Caregiver Visit Notes ({visitNotes.length})</h3>
            <p className="text-sm text-slate-500">Daily visit documentation</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setWeeklySheetDialogOpen(true)}
              className="gap-2"
              data-testid="generate-weekly-sheet-btn"
            >
              <FileText className="w-4 h-4" />
              Weekly Master Sheet
            </Button>
            <Button
              size="sm"
              onClick={onAddNote}
              className="gap-2"
              data-testid="new-visit-note-btn"
            >
              <Plus className="w-4 h-4" />
              New Daily Visit Note
            </Button>
          </div>
        </div>
        
        {visitNotes.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No visit notes recorded</p>
            <p className="text-xs mt-1">Click &quot;New Daily Visit Note&quot; to document each visit</p>
          </div>
        ) : (
          <div className="space-y-4">
            {visitNotes.map((note) => (
              <div
                key={note.id}
                className="p-4 border border-slate-200 rounded-lg"
                data-testid={`visit-note-${note.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">
                        {note.visit_date}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {note.time_in && note.time_out ? (
                          <span>Time In: {note.time_in} • Time Out: {note.time_out}</span>
                        ) : note.visit_time ? (
                          <span>at {note.visit_time}</span>
                        ) : null}
                        {note.duration_minutes && ` • ${note.duration_minutes} min`}
                      </p>
                      <p className="text-xs text-slate-400">
                        {getVisitTypeLabel(note.visit_type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      note.patient_condition === 'improved' 
                        ? 'bg-emerald-100 text-emerald-700'
                        : note.patient_condition === 'declined'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {note.patient_condition || 'Stable'}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      (note.caregiver_signature && note.client_signature)
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {(note.caregiver_signature && note.client_signature) ? 'Signed' : 'Draft'}
                    </span>
                  </div>
                </div>
                
                {/* Vital Signs */}
                {note.vital_signs && Object.values(note.vital_signs).some(v => v) && (
                  <div className="mb-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-500 mb-2">Vital Signs</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {note.vital_signs.blood_pressure && (
                        <div>
                          <span className="text-slate-500">BP:</span>{" "}
                          <span className="font-medium">{note.vital_signs.blood_pressure}</span>
                        </div>
                      )}
                      {note.vital_signs.heart_rate && (
                        <div>
                          <span className="text-slate-500">HR:</span>{" "}
                          <span className="font-medium">{note.vital_signs.heart_rate} bpm</span>
                        </div>
                      )}
                      {note.vital_signs.temperature && (
                        <div>
                          <span className="text-slate-500">Temp:</span>{" "}
                          <span className="font-medium">{note.vital_signs.temperature}°F</span>
                        </div>
                      )}
                      {note.vital_signs.oxygen_saturation && (
                        <div>
                          <span className="text-slate-500">O2:</span>{" "}
                          <span className="font-medium">{note.vital_signs.oxygen_saturation}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Services */}
                {note.services_provided?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">Services Provided</p>
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(note.services_provided) ? note.services_provided : [note.services_provided]).map((service, idx) => (
                        <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* MOTs/Observations */}
                {(note.mots_observations || note.clinical_notes) && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">MOTs/Observations</p>
                    <p className="text-sm text-slate-700">{note.mots_observations || note.clinical_notes}</p>
                  </div>
                )}
                
                {/* Follow Up */}
                {note.follow_up_needed && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="font-medium text-amber-700">Follow-up Required</p>
                    {note.follow_up_notes && (
                      <p className="text-amber-600 text-xs mt-1">{note.follow_up_notes}</p>
                    )}
                  </div>
                )}

                {/* Signatures */}
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Documented by: {note.employee_name}</span>
                    <div className="flex gap-3">
                      {note.caregiver_signature && <span className="text-emerald-600">✓ Caregiver</span>}
                      {note.client_signature && <span className="text-emerald-600">✓ Client</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Master Sheet Dialog */}
      <Dialog open={weeklySheetDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Weekly Master PCW Sheet</DialogTitle>
            <DialogDescription>
              Generate and sign the weekly summary for RN review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Week Start Date (Sunday)</Label>
              <Input
                type="date"
                value={weekStartDate}
                onChange={(e) => setWeekStartDate(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                The sheet will include all visits from this date through the following Saturday.
              </p>
            </div>
            
            {/* Signature Status */}
            {loadingSignature ? (
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-sm text-slate-500">Checking signature status...</p>
              </div>
            ) : existingSignature ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-800">RN Signature on File</p>
                </div>
                <p className="text-xs text-emerald-700">
                  Signed by <strong>{existingSignature.rn_name}</strong> on {new Date(existingSignature.rn_signed_date).toLocaleDateString()}
                </p>
                {existingSignature.rn_signature && (
                  <div className="mt-2 p-2 bg-white rounded border border-emerald-200">
                    <img 
                      src={existingSignature.rn_signature} 
                      alt="RN Signature" 
                      className="max-h-12 mx-auto"
                    />
                  </div>
                )}
              </div>
            ) : canSign ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <p className="text-sm font-medium text-amber-800">No RN Signature Yet</p>
                </div>
                <p className="text-xs text-amber-700">
                  This week&apos;s Master PCW Sheet has not been signed by an RN.
                </p>
              </div>
            ) : (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-600">
                  No RN signature on file. Only RN or Admin can sign the sheet.
                </p>
              </div>
            )}

            {/* RN Signature Pad (shown when signing) */}
            {showSignaturePad && canSign && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <PenTool className="w-4 h-4 text-primary" />
                  <Label className="font-medium">RN Signature</Label>
                </div>
                <SignaturePad
                  onSave={handleSignatureCapture}
                  onClear={handleClearSignature}
                  width={380}
                  height={120}
                />
                {rnSignature && (
                  <p className="text-xs text-emerald-600 text-center">✓ Signature captured</p>
                )}
              </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> The generated PDF includes:
              </p>
              <ul className="text-xs text-blue-700 mt-2 list-disc list-inside">
                <li>Daily time in/out records</li>
                <li>Services provided each day</li>
                <li>MOTs/Observations summary</li>
                <li>Total care hours</li>
                <li>RN signature (if signed)</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            
            {/* RN Sign Button - only show if user can sign and not already signed */}
            {canSign && !existingSignature && !showSignaturePad && (
              <Button 
                variant="secondary" 
                onClick={() => setShowSignaturePad(true)}
                className="gap-2"
                data-testid="rn-sign-sheet-btn"
              >
                <PenTool className="w-4 h-4" />
                RN Sign Sheet
              </Button>
            )}
            
            {/* Submit Signature Button */}
            {showSignaturePad && (
              <Button 
                onClick={handleRnSign} 
                disabled={signingSheet || !rnSignature}
                className="gap-2"
                data-testid="submit-rn-signature-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                {signingSheet ? "Signing..." : "Submit Signature"}
              </Button>
            )}
            
            <Button 
              onClick={handleGenerateWeeklySheet} 
              disabled={generatingSheet} 
              className="gap-2"
              data-testid="generate-pdf-btn"
            >
              <Download className="w-4 h-4" />
              {generatingSheet ? "Generating..." : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
